import React, { useState, useEffect } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import { getApiError } from '../../utils/apiError';

interface EmailSendDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
  meetingId?: number;
  /** Set together with context="LEAD". Leads reuse this dialog rather than
   *  getting one of their own: the screen is identical and a second copy is a
   *  second place for the preview logic to drift. */
  leadId?: number;
  patientName: string;
  patientEmail: string | null;
  context?: 'PATIENT' | 'MEETING' | 'LEAD';
  onSendSuccess?: (message: string) => void;
  onSendError?: (error: string) => void;
}

const EmailSendDialog: React.FC<EmailSendDialogProps> = ({
  open,
  onClose,
  patientId,
  meetingId,
  leadId,
  patientName,
  patientEmail,
  context = 'PATIENT',
  onSendSuccess,
  onSendError,
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variableMap, setVariableMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadTemplates();
      loadEntityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const allTemplates = await emailTemplateService.getTemplates();
      const contextTemplates = allTemplates.filter(
        (t) => t.context === context && t.is_active
      );
      setTemplates(contextTemplates);
      if (contextTemplates.length > 0) {
        setSelectedTemplateId(contextTemplates[0].id);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load email templates'));
      if (onSendError) onSendError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const loadEntityData = async () => {
    try {
      if (context === 'PATIENT' && patientId) {
        const { patientService } = await import('../../services/patientService');
        const { clinicService } = await import('../../services/clinicService');
        const patient = await patientService.getPatient(patientId);
        const clinic = await clinicService.getClinic(patient.clinic_id);
        const scheduleUrl = patient.uuid
          ? `${window.location.origin}/schedule/${patient.uuid}`
          : '';
        setVariableMap({
          first_name: patient.first_name,
          last_name: patient.last_name,
          phone: patient.phone ?? '',
          email: patient.email ?? '',
          id_number: patient.id_number ?? '',
          hm_id: patient.hm_id ?? '',
          status: patient.status,
          clinic_name: clinic.name,
          schedule_url: scheduleUrl,
        });
      } else if (context === 'LEAD' && leadId) {
        const { leadService } = await import('../../services/leadService');
        const lead = await leadService.getLead(leadId);
        const map: Record<string, string> = {
          first_name: lead.first_name,
          last_name: lead.last_name,
          full_name: `${lead.first_name} ${lead.last_name}`.trim(),
          phone: lead.phone ?? '',
          email: lead.email ?? '',
          id_number: lead.id_number ?? '',
          status: lead.status,
          sub_clinic: lead.sub_clinic ?? '',
        };
        // The tenant's own fields, under the same lead_field_* names the
        // backend resolves them as, so the preview matches what is sent.
        Object.entries(lead.additional_fields ?? {}).forEach(([key, value]) => {
          const sanitized = key.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
          map[`lead_field_${sanitized}`] = value == null ? '' : String(value);
        });
        setVariableMap(map);
      } else if (context === 'MEETING' && meetingId) {
        const { meetingService } = await import('../../services/meetingService');
        const { patientService } = await import('../../services/patientService');
        const { clinicService } = await import('../../services/clinicService');
        const meeting = await meetingService.getMeeting(meetingId);
        const patient = await patientService.getPatient(meeting.patient_id);
        const clinic = await clinicService.getClinic(patient.clinic_id);
        const d = new Date(meeting.scheduled_at);
        const pad = (n: number) => String(n).padStart(2, '0');
        const nameParts = meeting.patient.full_name.split(' ');
        const scheduleUrl = patient.uuid
          ? `${window.location.origin}/schedule/${patient.uuid}`
          : '';
        setVariableMap({
          patient_first_name: nameParts[0] ?? '',
          patient_last_name: nameParts.slice(1).join(' ') ?? '',
          therapist_name: meeting.therapist.full_name,
          date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
          meeting_type_name: meeting.meeting_type?.name ?? '',
          meeting_url: meeting.yoman_url ?? '',
          team_url: meeting.team_url ?? '',
          notes: meeting.notes ?? '',
          clinic_name: clinic.name,
          schedule_url: scheduleUrl,
        });
      }
    } catch {
      // Non-critical — preview substitution is best-effort
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId || !patientEmail) return;

    try {
      setSending(true);
      setError(null);

      if (context === 'MEETING' && meetingId) {
        const { meetingService } = await import('../../services/meetingService');
        await meetingService.sendEmail(meetingId, Number(selectedTemplateId));
      } else if (context === 'PATIENT' && patientId) {
        const { patientService } = await import('../../services/patientService');
        await patientService.sendEmail(patientId, Number(selectedTemplateId));
      } else if (context === 'LEAD' && leadId) {
        const { leadService } = await import('../../services/leadService');
        await leadService.sendEmail(leadId, Number(selectedTemplateId));
      } else {
        throw new Error('Invalid context or missing ID');
      }

      const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
      if (onSendSuccess) {
        onSendSuccess(`Email "${selectedTemplate?.name}" sent successfully to ${patientName}`);
      }
      setSelectedTemplateId(templates.length > 0 ? templates[0].id : '');
      onClose();
    } catch (err: any) {
      const errorMessage = getApiError(err, 'Failed to send email');
      setError(errorMessage);
      if (onSendError) onSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const isRtl = (template: EmailTemplate) => {
    if (template.language === 'he') return true;
    // Fallback: detect Hebrew characters in subject or content
    const hebrewRange = /[\u0590-\u05FF]/;
    return hebrewRange.test(template.subject) || hebrewRange.test(template.html_content || '');
  };

  // An unresolved or empty variable renders as blank, matching what the backend
  // sends — the patient must never see a raw "{first_name}".
  const renderPreview = (html: string): string =>
    html.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_match, key) =>
      variableMap[key] == null ? '' : String(variableMap[key])
    );

  const subject = context === 'LEAD' ? 'Lead' : 'Patient';
  const title = `Send Email to ${subject}: ${patientName}${patientEmail ? `  Email: ${patientEmail}` : ''}`;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={title}
      isLoading={sending}
      width="max-w-2xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {!patientEmail && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            Patient does not have an email address
          </div>
        )}

        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#366A75] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
            No active email templates with {context} context found
          </div>
        ) : (
          <>
            <div>
              <label className="block text-[#366A75] font-medium mb-2">Email Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                disabled={sending}
                className={`${POPUP_STYLES.input} appearance-none`}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {selectedTemplate && (
              <div className="space-y-2">
                <label className="block text-[#366A75] font-medium">Preview</label>
                <div>
                  <p className="text-xs text-[#366A75] mb-1">Subject</p>
                  <input
                    type="text"
                    value={selectedTemplate.subject}
                    readOnly
                    dir={isRtl(selectedTemplate) ? 'rtl' : 'ltr'}
                    style={isRtl(selectedTemplate) ? { direction: 'rtl', textAlign: 'right' } : undefined}
                    className={`${POPUP_STYLES.input} opacity-80`}
                  />
                </div>
                {selectedTemplate.html_content && (
                  <div>
                    <p className="text-xs text-[#366A75] mb-1">Body</p>
                    <div
                      dir={isRtl(selectedTemplate) ? 'rtl' : 'ltr'}
                      style={isRtl(selectedTemplate) ? { direction: 'rtl', textAlign: 'right' } : undefined}
                      className="max-h-56 overflow-y-auto rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] px-4 py-2 text-sm"
                      dangerouslySetInnerHTML={{ __html: renderPreview(selectedTemplate.html_content) }}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={!selectedTemplateId || !patientEmail || sending || templates.length === 0}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default EmailSendDialog;
